/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { type Workflow, workflowsQuery } from '@/api/workflows';
import { Icon } from '@/components/ui/Icon';
import { Modal } from '@/components/ui/Modal';
import { Empty, Failed, Rows } from '@/components/ui/States';

/**
 * Choosing what kind of agreement to start.
 *
 * "New agreement" used to send somebody to the Workflows screen, which is
 * where workflows are *built* -- configuration, versions, a menu to remove
 * one -- and left them to find the button among all that. Starting an
 * agreement is a smaller question: which one, then go. So it is asked here,
 * over the list they were already looking at, and answered with one button.
 *
 * A template limited to roles the person does not hold is left out: it is
 * not theirs to start (AC-01).
 *
 * A workflow with no document is listed and cannot be started. Hiding it
 * would leave somebody looking for the offer letter they know exists; showing
 * it with a working button would send them to a form that fails at the end.
 */
export function StartAgreement({
  onClose,
  canManage = false,
}: {
  onClose: () => void;
  canManage?: boolean;
}) {
  const { data, isPending, isError, error, refetch } = useQuery(workflowsQuery());
  const [term, setTerm] = useState('');

  // Only the templates this person has access to (AC-01): one limited to
  // roles they do not hold is not theirs to start, and is not offered. The
  // server refuses it anyway (`API-9`).
  const all = (data ?? []).filter((w) => w.can_launch).sort(ready);
  const wanted = term.trim().toLowerCase();
  const shown = wanted
    ? all.filter(
        (w) =>
          w.name.toLowerCase().includes(wanted) ||
          w.owning_team.toLowerCase().includes(wanted),
      )
    : all;

  return (
    <Modal
      title="Start an agreement"
      onClose={onClose}
      footer={
        <>
          {canManage ? (
            <Link to="/workflows" className="btn btn-t" onClick={onClose}>
              Manage workflows
            </Link>
          ) : null}
          <span className="grow" />
          <button type="button" className="btn btn-q" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {isPending ? <Rows rows={3} /> : null}
      {isError ? <Failed error={error} onRetry={() => void refetch()} /> : null}

      {data && all.length === 0 ? (
        <Empty
          icon="grid"
          title="No workflows yet"
          detail={
            canManage
              ? 'An agreement is started from a workflow. Add one, and it appears here.'
              : 'An agreement is started from a workflow, and none has been set up yet.'
          }
          action={
            canManage ? (
              <Link to="/workflows" className="btn btn-p" onClick={onClose}>
                Add a workflow
              </Link>
            ) : undefined
          }
        />
      ) : null}

      {all.length > 0 ? (
        <>
          <p className="starts-lede">
            Pick the kind of agreement. You fill in its form next.
          </p>

          {/* Only worth a search box once there is something to search. */}
          {all.length > 5 ? (
            <div className="starts-find">
              <label className="sr" htmlFor="start-find">
                Find a workflow
              </label>
              <input
                id="start-find"
                className="inp"
                type="search"
                placeholder="Find a workflow"
                value={term}
                onChange={(event) => {
                  setTerm(event.target.value);
                }}
              />
            </div>
          ) : null}

          <ul className="starts" aria-label="Workflows you can start from">
            {shown.map((workflow) => (
              <li key={workflow.id} className="start-row">
                <span className="start-i" aria-hidden>
                  <Icon name="doc" size={16} />
                </span>
                <span className="start-t">
                  <b>{workflow.name}</b>
                  <small>
                    {workflow.owning_team}
                    {workflow.paper === 'counterparty' ? (
                      <>
                        {' · '}
                        <span className="tag tpl-paper">Counterparty paper</span>
                      </>
                    ) : null}
                    {workflow.has_document ? null : (
                      <>
                        {' · '}
                        <span className="start-flag">
                          {workflow.paper === 'counterparty'
                            ? 'Not published yet'
                            : 'No document yet'}
                        </span>
                      </>
                    )}
                  </small>
                </span>
                {workflow.has_document ? (
                  <Link
                    to="/workflows/$workflowId/launch"
                    params={{ workflowId: workflow.id }}
                    className="btn btn-a btn-sm"
                    aria-label={`Start ${workflow.name}`}
                    onClick={onClose}
                  >
                    Start
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="btn btn-q btn-sm"
                    disabled
                    aria-label={`${workflow.name} cannot be started: it has no document yet`}
                  >
                    Start
                  </button>
                )}
              </li>
            ))}
          </ul>

          {shown.length === 0 ? (
            <p className="starts-none">Nothing matches &ldquo;{term.trim()}&rdquo;.</p>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

/** Ones that can be started first, then by name, so the list reads as a menu. */
function ready(a: Workflow, b: Workflow): number {
  if (a.has_document !== b.has_document) return a.has_document ? -1 : 1;
  return a.name.localeCompare(b.name);
}
